import { Component, Element, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import {
  applySchemeDelegate,
  ColorableComponent,
  getColorOfNearestBackground,
  setCssVariablesDelegate
} from "../../../common/colorable-component";
import { StepSchemeDefinition, StepSchemeName, StepSize, StepState } from "./constants/step-types";
import {
  STATE_ACTIVE,
  STATE_DONE,
  STATE_INACTIVE,
  STATE_INCOMPLETE,
  STEP_SCHEME_CONFIG,
  STEP_SCHEME_MAP,
  STEPSIZE_MEDIUM
} from "./constants/step-constants";
import pfx from "../../../utils/style-class-prefix-helper";
import { IconSize } from "../../icon/constants/icon-types";
import { isMobile } from "../../../utils/screen-helper";
import { uniqueId } from "../../../utils/unique-id";
import { ConditionalWrapper } from "../../../common/conditional-wrapper";
import { LinkTarget } from "../../link/constants/link-types";
import { StepSchemeConfiguration } from "../../../constants/colors";
import { STEP_PICTOGRAM_SIZE } from "../../../constants/global-constants";
import { StepBarVariants } from "../constants/steps-types";
import { STEP_BAR_CIRCLE, STEP_BAR_PICTOGRAM } from "../constants/steps-constants";
import { setCssVariable } from "../../../utils/css-variables";
import { BackgroundColor } from "../../background/constants/background-types";
import { StyledComponent } from "../../../decorators/styled-component";
// import { InitLogging } from "../../../decorators/init-logging";

@Component({
  tag: "eon-ui-step",
  styleUrl: "step.scss",
  shadow: true
})
export class Step implements ColorableComponent<StepSchemeConfiguration<StepSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: StepSchemeConfiguration<StepSchemeDefinition> = STEP_SCHEME_CONFIG;
  schemeMap = STEP_SCHEME_MAP;

  private stepId: string;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: StepSchemeDefinition;

  @State() iconSize: IconSize;
  @State() isMobileDevice: boolean;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiStepElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: StepSchemeName;

  /**
   * The size of the steps, if not provided the size is handled automatically whether the step is active or not
   */
  @Prop({ reflect: true, mutable: true }) size: StepSize = STEPSIZE_MEDIUM;

  /**
   * Icon to be shown in the middle of the step (circle variant)
   */
  @Prop() icon: string;

  /**
   * Pictogram to be shown in the middle of the step (pictogram variant)
   */
  @Prop() pictogram: string;

  /**
   * Text to be shown under the step
   */
  @Prop() text: string;

  /**
   * Text to be shown under the step
   */
  @Prop() error?: boolean;

  /**
   * to set step Index
   */
  @Prop() stepIndex?: number;

  /**
   * Control the state of the component
   */
  @Prop() state: StepState = STATE_INACTIVE;

  /**
   * The target of the href
   */
  @Prop() target: LinkTarget;

  /**
   * If a href is provided, the step is wrapped with a link to the target
   */
  @Prop() href: string;

  /**
   * Icon which should be shown when the state is "done", default ist "radio_checkbox_tick"
   */
  @Prop() doneIcon: string = "radio_checkbox_tick";

  /**
   * Vertical styling
   */
  @Prop({ reflect: true, mutable: true }) isVertical: boolean;

  /**
   * If true the label is hidden.
   */
  @Prop() hideLabel: boolean = false;

  /**
   * To set the visibility of tooltip for active step
   */
  @Prop({ reflect: true }) tooltipOnActiveStep?: boolean;

  /**
   * set the variant for Step
   */
  @Prop({ reflect: true, mutable: true }) variant: StepBarVariants = STEP_BAR_PICTOGRAM;

  /**
   * This is needed to shorten and position connector.
   */
  @Prop() firstItem: boolean;

  /**
   * This is needed to shorten and position connector.
   */
  @Prop() lastItem: boolean;

  /**
   * If numbered, icons and pictograms are going to be replaced by step index.
   */
  @Prop() numbered: boolean;

  /**
   * If true, a checkmark will be visible on done steps.
   */
  @Prop() checkmarkWhenDone: boolean;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  componentWillLoad() {
    this.applyScheme();
    this.setIconSize();
    this.onViewportSizeChange();
    this.stepId = uniqueId();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.isMobileDevice = isMobile();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("size")
  setIconSize() {
    switch (this.size) {
      case "small":
        this.iconSize = "small";
        break;
      case "medium":
        this.iconSize = "normal";
        break;
      case "large":
        this.iconSize = "normal";
        break;
      default:
        this.iconSize = "normal";
        break;
    }
  }

  @Listen("resize", { target: "window" })
  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    const sectionsOnPage = this.hostElement.shadowRoot.querySelectorAll(".eonui-circle-step-wrapper");
    setCssVariable(this.hostElement, "--tooltipWidth", `${(sectionsOnPage[0] as HTMLElement)?.clientWidth - 8}px`);
    setCssVariable(
      this.hostElement,
      "--parentBackgroundColor",
      `var(--${getColorOfNearestBackground(this.hostElement)})`
    );
  }

  private getStepRendering() {
    switch (this.state) {
      case STATE_ACTIVE:
        return (
          <div
            class={pfx({
              container: true,
              active: true,
              error: this.error
            })}
          >
            <div class={pfx({ carrier: true })}></div>

            {this.error ? (
              <eon-ui-icon
                class={pfx({ "step-icon": true })}
                size={this.iconSize}
                name={"close"}
                alignmentX="center"
                alignmentY="center"
              ></eon-ui-icon>
            ) : (
              <eon-ui-pictogram
                class={pfx({
                  pictogram: true,
                  active: true
                })}
                name={this.pictogram}
                alignmentX="center"
                alignmentY="center"
                size={STEP_PICTOGRAM_SIZE}
              ></eon-ui-pictogram>
            )}
          </div>
        );
      case STATE_INACTIVE:
        return (
          <div
            class={pfx({
              container: true,
              inactive: true
            })}
          >
            <div class={pfx({ carrier: true })}></div>
            <eon-ui-pictogram
              class={pfx({
                pictogram: true,
                inactive: true
              })}
              name={this.pictogram}
              alignmentX="center"
              alignmentY="center"
              size={STEP_PICTOGRAM_SIZE}
            ></eon-ui-pictogram>
          </div>
        );
      case STATE_DONE:
        return (
          <div class={pfx({ container: true, done: true, error: this.error })}>
            <div class={pfx({ carrier: true })}></div>
            {this.error ? (
              <eon-ui-icon
                class={pfx({ "step-icon": true })}
                size={this.iconSize}
                name={"close"}
                alignmentX="center"
                alignmentY="center"
              ></eon-ui-icon>
            ) : (
              <eon-ui-icon
                class={pfx({ icon: true, done: true })}
                name={this.doneIcon}
                size="normal"
                alignmentX="center"
                alignmentY="center"
              ></eon-ui-icon>
            )}
          </div>
        );
    }
  }

  renderStepIcon() {
    let icon;

    if (this.error && this.state === STATE_DONE) {
      icon = <eon-ui-icon class={pfx({ "step-icon": true })} size={this.iconSize} name={"close"}></eon-ui-icon>;
    } else if (this.state === STATE_DONE && this.checkmarkWhenDone) {
      icon = (
        <eon-ui-icon class={pfx({ "step-icon": true })} size={this.iconSize} name={"radio_checkbox_tick"}></eon-ui-icon>
      );
    } else if (this.error && this.state === STATE_ACTIVE) {
      icon = <eon-ui-icon class={pfx({ "step-icon": true })} size={this.iconSize} name={"close"}></eon-ui-icon>;
    } else if (this.numbered && typeof this.stepIndex === "number") {
      icon = <span class={pfx({ "step-number": true })}>{this.stepIndex + 1}</span>;
    } else if (this.icon) {
      icon = <eon-ui-icon class={pfx({ "step-icon": true })} size={this.iconSize} name={this.icon}></eon-ui-icon>;
    } else {
      icon = "";
    }

    return icon;
  }

  render() {
    return (
      <Host>
        {this.variant === STEP_BAR_PICTOGRAM && (
          <li
            class={pfx({
              "pictogram-step-wrapper": true,
              vertical: this.isVertical || this.isMobileDevice,
              "first-item": this.firstItem,
              "last-item": this.lastItem,
              inactive: this.state === STATE_INACTIVE
            })}
            aria-labelledby={this.stepId}
          >
            <div class={pfx({ wrapper: true })}>
              <div
                class={pfx({
                  connector: true,
                  active: this.state === STATE_ACTIVE,
                  inactive: this.state === STATE_INACTIVE,
                  done: this.state === STATE_DONE,
                  error: this.error
                })}
              ></div>
              {/* Must be on the top because how current selector for link is implemented */}
              <ConditionalWrapper
                condition={!!this.href}
                wrapper={(children) => (
                  <a href={this.href} target={this.target}>
                    {children}
                  </a>
                )}
                defaultWrapper={(children) => children}
              >
                {!this.isMobileDevice && !this.isVertical && this.getStepRendering()}
                {(this.isMobileDevice || this.isVertical) && (
                  <div
                    class={pfx({
                      mobile: true,
                      active: this.state === STATE_ACTIVE,
                      done: this.state === STATE_DONE,
                      error: this.error
                    })}
                  >
                    {this.error && (
                      <eon-ui-icon class={pfx({ "step-icon": true })} size={"normal"} name={"close"}></eon-ui-icon>
                    )}
                  </div>
                )}
              </ConditionalWrapper>

              <div
                class={pfx({
                  description: true,
                  active: this.state === STATE_ACTIVE,
                  done: this.state == STATE_DONE,
                  "visibility-none": this.hideLabel
                })}
              >
                {this.text}
              </div>
            </div>
            <div id={this.stepId} aria-hidden="true" hidden>
              Step status: {this.state === STATE_INACTIVE ? "upcoming" : this.state}
            </div>
          </li>
        )}
        {this.variant === STEP_BAR_CIRCLE && (
          <li
            class={pfx({
              "circle-step-wrapper": true,
              "first-item": this.firstItem,
              "last-item": this.lastItem,
              vertical: this.isVertical,
              active: this.state === STATE_ACTIVE,
              done: this.state === STATE_DONE,
              incomplete: this.state === STATE_INCOMPLETE,
              inactive: this.state === STATE_INACTIVE,
              error: this.error,
              [`size-${this.size}`]: this.size,
              numbered: this.numbered,
              icon: this.icon,
              "tooltip-on-step": this.tooltipOnActiveStep
            })}
            step-index={this.stepIndex}
            aria-labelledby={this.stepId}
          >
            <div
              class={pfx({
                connector: true
              })}
              id="connector"
            ></div>
            {/* Must be on the top because how current selector for link is implemented */}
            <ConditionalWrapper
              condition={!!this.href}
              wrapper={(children) => (
                <a href={this.href} target={this.target}>
                  {children}
                </a>
              )}
              defaultWrapper={(children) => children}
            >
              <div
                class={pfx({
                  circle: true
                })}
                /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-static-element-interactions */
              >
                {this.tooltipOnActiveStep && (
                  <eon-ui-tooltip
                    slot="tooltip"
                    size="small"
                    noTransitionDelay={true}
                    visibility={this.state === STATE_ACTIVE ? "always" : "onHover"}
                    placement={this.isVertical ? "right" : "top"}
                    scheme={this.activeScheme.tooltipScheme}
                    optimizedPlacement={false}
                    moveTooltipToEndOfBody={true}
                  >
                    <eon-ui-text text-style="copy">{this.text}</eon-ui-text>
                  </eon-ui-tooltip>
                )}
                {this.renderStepIcon()}
              </div>
            </ConditionalWrapper>
            <p
              class={pfx({
                "step-label": true,
                "visibility-none": this.hideLabel
              })}
            >
              {this.text}
            </p>
            <div id={this.stepId} aria-hidden="true" hidden>
              Step status: {this.state === STATE_INACTIVE ? "upcoming" : this.state}
            </div>
          </li>
        )}
      </Host>
    );
  }
}
